/**
 * Vulnerabilities Table Component
 *
 * Displays detailed, filterable, sortable list of scan findings.
 */

import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Filter, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import type { LLMScanResult, LLMScanStatus, SeverityLevel, LLMScanResultStatus, ResultFilters } from "@/types/llm";
import { getSeverityColor, truncateText } from "@/utils/llmFormatters";
import { formatPluginName } from "@/utils/llmFormat";
import { FilterDropdown } from "./FilterDropdown";

interface VulnerabilitiesTableProps {
  results: LLMScanResult[];
  isLoading?: boolean;
  scanStatus?: LLMScanStatus;
  onResultClick?: (result: LLMScanResult) => void;
  onFilterChange?: (filters: ResultFilters & { search?: string }) => void;
  page?: number;
  itemsPerPage?: number;
  onPageChange?: (page: number) => void;
  totalCount?: number;
  totalPages?: number;
}

export const VulnerabilitiesTable: React.FC<VulnerabilitiesTableProps> = ({
  results,
  isLoading = false,
  scanStatus,
  onResultClick,
  onFilterChange,
  page = 1,
  itemsPerPage = 10,
  onPageChange,
  totalCount,
  totalPages,
}) => {
  // Determine if server-side filtering and pagination is enabled
  const isServerSide = totalCount !== undefined || totalPages !== undefined;
  const [severityFilter, setSeverityFilter] = useState<SeverityLevel | undefined>();
  const [statusFilter, setStatusFilter] = useState<LLMScanResultStatus | undefined>();
  const [pluginFilter, setPluginFilter] = useState<string | undefined>();
  const [searchQuery, setSearchQuery] = useState("");

  // Reset to page 1 when filters change
  useEffect(() => {
    if (onPageChange && page !== 1) {
      onPageChange(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [severityFilter, statusFilter, pluginFilter, searchQuery]);

  const handleSeverityFilter = (severity: SeverityLevel | "all") => {
    const newFilter = severity === "all" ? undefined : severity;
    setSeverityFilter(newFilter);
    onFilterChange?.({
      severity: newFilter,
      status: statusFilter,
      plugin_id: pluginFilter,
      search: searchQuery || undefined,
    });
  };

  const handleStatusFilter = (status: LLMScanResultStatus | "all") => {
    const newFilter = status === "all" ? undefined : status;
    setStatusFilter(newFilter);
    onFilterChange?.({
      severity: severityFilter,
      status: newFilter,
      plugin_id: pluginFilter,
      search: searchQuery || undefined,
    });
  };

  const handlePluginFilter = (pluginId: string | "all") => {
    const newFilter = pluginId === "all" ? undefined : pluginId;
    setPluginFilter(newFilter);
    onFilterChange?.({
      severity: severityFilter,
      status: statusFilter,
      plugin_id: newFilter,
      search: searchQuery || undefined,
    });
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    onFilterChange?.({
      severity: severityFilter,
      status: statusFilter,
      plugin_id: pluginFilter,
      search: query,
    });
  };

  // Get unique plugin IDs for filter dropdown (only needed for client-side filtering)
  const uniquePlugins = Array.from(new Set(results.map((r) => r.plugin_id)));

  // Filter and paginate results
  let filteredResults: LLMScanResult[];
  let paginatedResults: LLMScanResult[];
  let finalTotalPages: number;
  let finalTotalCount: number;
  let startIndex: number;
  let endIndex: number;

  if (isServerSide) {
    // Server-side: results are already filtered and paginated
    filteredResults = results;
    paginatedResults = results;
    finalTotalPages = totalPages ?? Math.ceil((totalCount ?? 0) / itemsPerPage);
    finalTotalCount = totalCount ?? results.length;
    // Calculate indices for display purposes
    startIndex = (page - 1) * itemsPerPage;
    endIndex = startIndex + itemsPerPage;
  } else {
    // Client-side: filter and paginate locally
    filteredResults = results.filter((result) => {
      if (severityFilter && result.severity !== severityFilter) return false;
      if (statusFilter && result.status !== statusFilter) return false;
      if (pluginFilter && result.plugin_id !== pluginFilter) return false;
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch =
          result.plugin_id.toLowerCase().includes(query) ||
          result.goal?.toLowerCase().includes(query) ||
          result.reason?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }
      return true;
    });

    // Paginate filtered results
    finalTotalPages = Math.ceil(filteredResults.length / itemsPerPage);
    finalTotalCount = filteredResults.length;
    startIndex = (page - 1) * itemsPerPage;
    endIndex = startIndex + itemsPerPage;
    paginatedResults = filteredResults.slice(startIndex, endIndex);
  }

  const hasActiveFilters = severityFilter || statusFilter || pluginFilter || searchQuery;

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-2">
          <Skeleton className="h-10 flex-1" />
          <Skeleton className="h-10 w-24" />
        </div>
        <div className="overflow-hidden rounded-[15px] border bg-white">
          <div className="p-4 space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (filteredResults.length === 0) {
    return (
      <div className="space-y-4">
        {/* Filters still visible when empty */}
        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            placeholder="Search by plugin, goal, or reason..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
            className="flex-1 text-sm"
          />
          <FilterDropdown
            severityFilter={severityFilter}
            statusFilter={statusFilter}
            pluginFilter={pluginFilter}
            uniquePlugins={uniquePlugins}
            onSeverityFilter={handleSeverityFilter}
            onStatusFilter={handleStatusFilter}
            onPluginFilter={handlePluginFilter}
          />
        </div>
        <div className="overflow-hidden rounded-[15px] border bg-white">
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            {hasActiveFilters ? (
              <>
                <Filter className="h-12 w-12 mb-4 text-muted-foreground opacity-50" />
                <p className="text-sm font-medium mb-1">No vulnerabilities match your filters</p>
                <p className="text-xs text-muted-foreground">
                  Try adjusting your search or filter criteria
                </p>
              </>
            ) : scanStatus === "failed" || scanStatus === "cancelled" ? (
              <>
                <AlertTriangle className="h-12 w-12 mb-4 text-red-500 opacity-50" />
                <p className="text-sm font-medium mb-1">Scan failed</p>
                <p className="text-xs text-muted-foreground">
                  No test results are available because the scan could not complete.
                </p>
              </>
            ) : scanStatus === "pending" || scanStatus === "running" ? (
              <>
                <Loader2 className="h-12 w-12 mb-4 text-blue-500 opacity-50 animate-spin" />
                <p className="text-sm font-medium mb-1">Scan in progress</p>
                <p className="text-xs text-muted-foreground">
                  Test results will appear here as the scan progresses.
                </p>
              </>
            ) : (
              <>
                <CheckCircle2 className="h-12 w-12 mb-4 text-green-500 opacity-50" />
                <p className="text-sm font-medium mb-1">No vulnerabilities found</p>
                <p className="text-xs text-muted-foreground">
                  Great! Your scan found no security issues.
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-2">
        <Input
          placeholder="Search by plugin, goal, or reason..."
          value={searchQuery}
          onChange={(e) => handleSearch(e.target.value)}
          className="flex-1 text-sm"
        />
        <FilterDropdown
          severityFilter={severityFilter}
          statusFilter={statusFilter}
          pluginFilter={pluginFilter}
          uniquePlugins={uniquePlugins}
          onSeverityFilter={handleSeverityFilter}
          onStatusFilter={handleStatusFilter}
          onPluginFilter={handlePluginFilter}
        />
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-[15px] border bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[150px] sm:w-[200px] text-xs sm:text-sm">Type</TableHead>
              <TableHead className="text-xs sm:text-sm">Description</TableHead>
              <TableHead className="w-[80px] sm:w-[100px] text-xs sm:text-sm">Severity</TableHead>
              <TableHead className="w-[80px] sm:w-[100px] text-xs sm:text-sm">Status</TableHead>
              <TableHead className="w-[100px] sm:w-[120px] text-xs sm:text-sm">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {paginatedResults.length > 0 ? (
              paginatedResults.map((result) => (
                <TableRow
                  key={result.uuid}
                  className="cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => onResultClick?.(result)}
                >
                  <TableCell className="font-medium text-xs sm:text-sm">
                    <span className="truncate block max-w-[150px] sm:max-w-[200px]">
                      {formatPluginName(result.plugin_id)}
                    </span>
                  </TableCell>
                  <TableCell className="max-w-md">
                    <div className="text-xs sm:text-sm text-muted-foreground">
                      {truncateText(result.goal || "No description", 60)}
                    </div>
                  </TableCell>
                  <TableCell>
                    {result.severity ? (
                      <Badge className={`${getSeverityColor(result.severity)} text-xs`}>
                        {result.severity}
                      </Badge>
                    ) : (
                      <span className="text-xs text-muted-foreground">N/A</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={result.status === "PASS" ? "default" : "destructive"}
                      className="text-xs"
                    >
                      {result.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-xs h-7 px-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        onResultClick?.(result);
                      }}
                    >
                      <span className="hidden sm:inline">View Details</span>
                      <span className="sm:hidden">View</span>
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-8 text-sm text-muted-foreground">
                  No results found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      {((isServerSide && finalTotalCount > itemsPerPage) || (!isServerSide && filteredResults.length > itemsPerPage)) &&
        onPageChange && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t">
            <div className="text-xs sm:text-sm text-muted-foreground">
              {isServerSide ? (
                <>
                  Showing {(page - 1) * itemsPerPage + 1}-
                  {Math.min(page * itemsPerPage, finalTotalCount)} of {finalTotalCount} results (Page {page} of{" "}
                  {finalTotalPages})
                </>
              ) : (
                <>
                  Showing {startIndex + 1}-{Math.min(endIndex, filteredResults.length)} of{" "}
                  {filteredResults.length} results (Page {page} of {finalTotalPages})
                </>
              )}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 1 || isLoading}
                onClick={() => onPageChange(Math.max(1, page - 1))}
                className="text-xs sm:text-sm"
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= finalTotalPages || isLoading}
                onClick={() => onPageChange(page + 1)}
                className="text-xs sm:text-sm"
              >
                Next
              </Button>
            </div>
          </div>
        )}
    </div>
  );
};
